import type { ExportColumnId } from 'components/superreports/ReportExport/types';
import { isAgentReportColumn } from 'constants/agentReporting';
import type { Column } from 'constants/filters';
import {
  CALCULATED_COLUMNS,
  CAPACITY_COLUMNS,
  COLUMN_BILLABLE_HOURS,
  COLUMN_BILLED_HOURS,
  COLUMN_BILLED_MONEY,
  COLUMN_BUDGET,
  COLUMN_BUDGET_REMAINING,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_EXPORT_ALIASES,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_LOGGED_PERCENTAGE,
  COLUMN_NON_BILLABLE_HOURS,
  COLUMN_UNBILLED_HOURS,
  COLUMN_UNBILLED_MONEY,
  COLUMN_UTILIZATION,
  COLUMNS_FOR_HOURS_DATA,
  COLUMNS_FOR_MONEY_DATA,
  DEFAULT_DONUT_AND_TIMELINE_COLUMNS,
  HOUR_STATE_BILLED,
  HOUR_STATE_BILLED_AND_LOCKED,
  HOUR_STATE_INVOICED,
  HOUR_STATE_LOCKED,
  HOUR_STATE_LOGGED,
  UTILIZATION_SOURCE_COLUMNS,
} from 'constants/filters';
import { EMPTY_COST, EMPTY_DURATION } from 'constants/reports';
import {
  DONUT_WIDGETS,
  TIMELINE_WIDGETS,
  WIDGET_CLIENTS_HBAR,
  WIDGET_CLIENTS_LIST,
  WIDGET_TEAMS_LIST,
} from 'constants/widgets';
import type { SupportedWidgetEntity } from 'factories/superreports';
import { difference, intersection, isEqual, without } from 'lodash-es';
import { DateTime } from 'luxon';
import memoizeOne from 'memoize-one';
import type { CSSProperties } from 'react';
import type { HourState } from 'redux/modules/hourStates.schema';
import type {
  BudgetDataEntity,
  CapacityDataEntity,
  CommonDataEntity,
  EmailedReport,
  GenericWidgetRowValues,
  ReportDraft,
  ReportFiltersWithoutLegacyProps,
  ReportProjectIds,
  ReportQuery,
  ReportSelection,
  WidgetDraft,
  WidgetMetadataDictionary,
  WidgetReference,
  WidgetWithData,
} from 'redux/modules/superreports.types';
import { CommonDataEntityKeys, WidgetDataType } from 'redux/modules/superreports.types';
import type { TagsWithChildrenDictionary } from 'redux/modules/tags.types';
import { getFullProjectOrNull } from 'redux/selectors/utils/projects';
import { cssVariables } from 'styles';
import type { Currency, Features } from 'types/account';
import type { Hour, HourTimestamp } from 'types/hour';
import type { ProjectVariant, ProjectVariantMap } from 'types/project';
import type { EntityDataResponse } from 'types/reports';
import type { Tag } from 'types/tag';
import { formatCurrency } from 'utils/currency';
import { naturalCompare } from 'utils/naturalSort';
import { getUtilizationPercentage } from 'utils/utilization';
import { ViewportWidthBreakpoint } from 'utils/viewport';
import type { CostAndDurationSummary } from './summary';
import { notNull } from './typesafe';

export function sortWidgetData(
  a: CommonDataEntity,
  b: CommonDataEntity,
  sort_by: string,
  order: 'asc' | 'desc'
) {
  if (isAgentReportColumn(sort_by)) {
    const av = a.agent_activity?.[sort_by];
    const bv = b.agent_activity?.[sort_by];
    if (av == null) return bv == null ? 0 : 1;
    if (bv == null) return -1;
    return (av - bv) * (order === 'asc' ? 1 : -1);
  }
  let aRecord;
  let bRecord;

  switch (sort_by) {
    case COLUMN_LOGGED_PERCENTAGE:
    case COLUMN_LOGGED_HOURS: {
      aRecord = a.duration?.total_minutes ?? 0;
      bRecord = b.duration?.total_minutes ?? 0;
      break;
    }

    case COLUMN_ESTIMATED_HOURS: {
      aRecord = a.estimated_duration?.total_minutes ?? 0;
      bRecord = b.estimated_duration?.total_minutes ?? 0;
      break;
    }

    case COLUMN_BILLED_HOURS: {
      aRecord = a.billed_duration?.total_minutes;
      bRecord = b.billed_duration?.total_minutes;
      break;
    }

    case COLUMN_BILLABLE_HOURS: {
      aRecord = a.billable_duration?.total_minutes;
      bRecord = b.billable_duration?.total_minutes;
      break;
    }

    case COLUMN_NON_BILLABLE_HOURS: {
      aRecord = a.non_billable_duration?.total_minutes;
      bRecord = b.non_billable_duration?.total_minutes;
      break;
    }

    case COLUMN_UNBILLED_HOURS: {
      aRecord = a.unbilled_duration?.total_minutes;
      bRecord = b.unbilled_duration?.total_minutes;
      break;
    }

    case COLUMN_LOGGED_MONEY: {
      aRecord = a.cost?.amount ?? 0;
      bRecord = b.cost?.amount ?? 0;
      break;
    }

    case COLUMN_BILLED_MONEY: {
      aRecord = a.billed_cost?.amount ?? 0;
      bRecord = b.billed_cost?.amount ?? 0;
      break;
    }

    case COLUMN_UNBILLED_MONEY: {
      aRecord = a.unbilled_cost?.amount ?? 0;
      bRecord = b.unbilled_cost?.amount ?? 0;
      break;
    }

    case COLUMN_ESTIMATED_MONEY: {
      aRecord = a.estimated_cost?.amount ?? 0;
      bRecord = b.estimated_cost?.amount ?? 0;
      break;
    }

    case COLUMN_UTILIZATION: {
      aRecord = getUtilizationPercentage(
        a.billable_duration?.total_minutes,
        (a as CapacityDataEntity).capacity?.total_minutes
      );
      bRecord = getUtilizationPercentage(
        b.billable_duration?.total_minutes,
        (b as CapacityDataEntity).capacity?.total_minutes
      );
      break;
    }

    case COLUMN_BUDGET: {
      aRecord = (a as BudgetDataEntity).budget;
      bRecord = (b as BudgetDataEntity).budget;
      break;
    }

    case COLUMN_BUDGET_REMAINING: {
      const aBudget = a as BudgetDataEntity;
      const bBudget = b as BudgetDataEntity;
      aRecord = aBudget.budget ? aBudget.budget - (aBudget.budget_progress ?? 0) : -Infinity;
      bRecord = bBudget.budget ? bBudget.budget - (bBudget.budget_progress ?? 0) : -Infinity;

      // NaturalCompare doesn't handle Infinity particularly well, and we use Infinity to push
      // items without a budget to the bottom
      return bRecord - aRecord * (order === 'asc' ? -1 : 1);
    }
    default: {
      aRecord = a[sort_by as any as keyof typeof a] as any;
      bRecord = b[sort_by as any as keyof typeof b] as any;
    }
  }

  return order === 'asc' ? naturalCompare(aRecord, bRecord) : naturalCompare(bRecord, aRecord);
}

// Generates a signature that we can compare against to determine if
// the report draft has unsaved changes or not.
// There's probably a lot of room for improvement here?
export function getSignatureFromDraftAndQuery(
  draftSeed: Partial<ReportDraft> = {},
  query: any = {}
) {
  const {
    widgets,
    hasLoadedInitialData,
    live,
    events,
    multiCurrency,
    downloaded_reports,
    ...draft
  } = draftSeed;

  // ignore widget._status in signature.
  const widgetSansStatus = (widgets || []).map(widget => {
    const { _status, ...rest } = widget;
    return rest;
  });

  const { from, to, ...strippedQuery } = query;
  const span = query.span === 'custom' ? 'day' : query.span;

  // Only strip dates for relative date ranges
  const isRelativeDateRange = query.relativeDateRange !== undefined;
  if (!isRelativeDateRange) {
    return JSON.stringify({ draft, query, widgets: widgetSansStatus });
  }

  // For relative ranges, strip dates if they're in the same period
  if (!DateTime.fromISO(from).hasSame(DateTime.fromISO(to), span)) {
    return JSON.stringify({ draft, query, widgets: widgetSansStatus });
  }

  // In all other cases, ignore from/to when comparing signatures
  return JSON.stringify({ draft, query: strippedQuery, widgets: widgetSansStatus });
}

// When we're selecting tags for batch actions we want to be very strict so as users cannot attempt
// to apply tags that aren't common to all selections
export function getCommonTagsForSelection(
  tags: Array<Tag>,
  projectIds: Array<number>,
  projects: ProjectVariantMap
) {
  const labelIds = projectIds.map(project => getFullProjectOrNull(projects[project])?.label_ids);

  if (!labelIds || labelIds.length === 0) {
    return [];
  }

  const intersectingLabelIds = labelIds.length > 1 ? intersection(...labelIds) : labelIds[0];
  return tags.filter(tag => tag.id && intersectingLabelIds?.includes(tag.id));
}

export const createWidgetElementId = (reference: WidgetReference) => `widget-${reference}`;

/**
 * When we're selecting tags for filtering we want to be less strict so as users can filter
 * by as many relevant tags as they prefer. Return all tags and their children based on the labelIds
 * (including parents & children) that we're provided.
 *
 * @param allLoadedTags: a dictionary of tags
 * @param tagIds: a list of tag arrays, each of which is derived from a project's label_ids
 * @param savedTagIds: a list of tags which have been saved to relevant hours,
 * used to check if there is a disparity between what's available (tagIds) and what has actually been saved
 *
 * @returns an array of tags common to each array in tagIds
 */

export function getCommonTagsForFiltering(
  allLoadedTags: TagsWithChildrenDictionary,
  tagIds: Array<Array<number>>,
  savedTagIds?: Array<number>
): Array<Tag> {
  if (!tagIds || tagIds.length === 0) {
    return [];
  }

  const ids = tagIds.flat();
  // are there labels inside savedTagIds that aren't in tagIds? If so, we want to look those up too
  const excludedTagIds = difference(savedTagIds || [], ids);
  const deDupedIds = [...new Set([...ids, ...excludedTagIds])];

  return deDupedIds
    .map(id => {
      const tag = allLoadedTags[id];
      const parentHasBeenRemoved = excludedTagIds.includes(id);

      if (tag?.children && tag.children.length > 0) {
        return {
          ...tag,
          hasBeenRemoved: parentHasBeenRemoved,
          children: tag.children
            .filter(child => deDupedIds?.includes(child.id))
            .map(c => {
              if (excludedTagIds.includes(c.id)) {
                return {
                  ...c,
                  hasBeenRemoved: true,
                };
              }
              return c;
            }),
        };
      }

      return { ...tag, hasBeenRemoved: parentHasBeenRemoved };
    })
    .filter(tag => tag && !tag.parent_id);
}

/**
 * Appends the calculated columns a widget can support to its available columns.
 *
 * @description
 * Calculated columns are derived on the frontend from other columns in the same row, so the API
 * doesn't list them in the widget metadata. A widget offers a calculated column exactly when it
 * provides every column that calculation needs.
 */
export const withCalculatedColumns = (columns: Array<string>): Array<string> =>
  UTILIZATION_SOURCE_COLUMNS.every(column => columns.includes(column))
    ? [...columns, COLUMN_UTILIZATION]
    : columns;

/**
 * Filters columns based on the widget type and billed state filter.
 */
export const filterAvailableColumns = (
  columns: Array<string>,
  widgetType: string,
  billedState: boolean | null,
  isCapacityReportsFeatureEnabled = false,
  readOnly = false
) => {
  let filteredColumns = columns;

  if (widgetType !== WIDGET_CLIENTS_LIST) {
    filteredColumns = filteredColumns.filter(
      name => ![COLUMN_BUDGET, COLUMN_BUDGET_REMAINING].includes(name)
    );
  }

  if (billedState !== null) {
    filteredColumns = filteredColumns.filter(
      name =>
        ![
          COLUMN_BILLED_HOURS,
          COLUMN_BILLED_MONEY,
          COLUMN_UNBILLED_HOURS,
          COLUMN_UNBILLED_MONEY,
        ].includes(name)
    );
  }

  if (!isCapacityReportsFeatureEnabled && !readOnly) {
    filteredColumns = filteredColumns.filter(name => !CAPACITY_COLUMNS.includes(name));
  }

  return filteredColumns;
};

/**
 * Checks if the response means that the report was emailed to the user.
 */
export const isEmailedReport = (response: any): response is EmailedReport =>
  response?.sent_via_email && !!response.email;

export const extractPublicReportId = (url: string): string => {
  const parts = new URL(url).pathname.split('/');
  return parts[parts.length - 1];
};

/**
 * Checks if the provided entity is a data entity (meaning it can be reported on) or if it's a
 * partial entity derived from somewhere else.
 */
export const isDataEntity = (entity?: SupportedWidgetEntity) =>
  entity
    ? Object.keys(entity).some(key => CommonDataEntityKeys.includes(key as keyof CommonDataEntity))
    : false;

/**
 * Extracts the summarized totals from the totals returned from the server.
 */
export const getSummaryFromTotals = (
  totals?: CommonDataEntity,
  currencyLookup?: Record<string, Currency | undefined>
): CostAndDurationSummary => {
  const summary: CostAndDurationSummary = {
    ...(totals?.agent_activity ? { agentActivity: totals.agent_activity } : {}),
    costBilled: totals?.billed_cost?.amount ?? 0,
    costLogged: totals?.cost?.amount ?? 0,
    costPlanned: totals?.estimated_cost?.amount ?? 0,
    costUnbilled: totals?.unbilled_cost?.amount ?? 0,
    minutesBilled: totals?.billed_duration?.total_minutes ?? 0,
    minutesLogged: totals?.duration?.total_minutes ?? 0,
    minutesPlanned: totals?.estimated_duration?.total_minutes ?? 0,
    minutesUnbilled: totals?.unbilled_duration?.total_minutes ?? 0,
    minutesBillable: totals?.billable_duration?.total_minutes ?? 0,
    minutesNonBillable: totals?.non_billable_duration?.total_minutes ?? 0,
    currency:
      currencyLookup?.[totals?.cost?.currency_code ?? totals?.billed_cost?.currency_code ?? ''],
  };

  if (!summary.costLogged) {
    summary.costLogged = summary.costBilled + summary.costUnbilled;
  }

  return summary;
};

/**
 * Verifies and rectifies widget columns.
 *
 * @description
 * New widgets will have the correct columns by default, so this is in practice a no-op for them.
 *
 * However, pre-existing reports that were created before these rules were in place will have a
 * default set of columns that don't match the data to be included. Instead of having to
 * retroactively fix this on the backend for every report ever created, we can take advantage of the
 * fact that we always save reports before publishing on the frontend, and set the correct columns
 * directly on the saved payload.
 */
export const reviseWidgetDraftColumns = (
  widgets: Array<WidgetDraft>,
  metadata: WidgetMetadataDictionary
) =>
  widgets.map(widget => {
    const metadataForWidget = metadata[widget.widget_type];
    if (!metadataForWidget) return widget;

    const isDonutOrTimelineWidget =
      DONUT_WIDGETS.includes(widget.widget_type) || TIMELINE_WIDGETS.includes(widget.widget_type);

    const widgetColumns = isDonutOrTimelineWidget
      ? DEFAULT_DONUT_AND_TIMELINE_COLUMNS
      : widget.columns.names;

    let revisedColumns = without(
      intersection(widgetColumns, metadataForWidget.columns),
      ...CALCULATED_COLUMNS
    );
    if (isDonutOrTimelineWidget) {
      if (widget.options?.dataType === WidgetDataType.Hours) {
        revisedColumns = without(revisedColumns, ...COLUMNS_FOR_MONEY_DATA);
      } else if (widget.options?.dataType === WidgetDataType.Money) {
        revisedColumns = without(revisedColumns, ...COLUMNS_FOR_HOURS_DATA);
      }
    }

    // The API rejects calculated columns as column names, so they're persisted as an option
    // instead. `newReportWidgetFactory` merges them back into the columns when the report loads.
    const calculatedColumns = intersection(widgetColumns, CALCULATED_COLUMNS);
    const revisedOptions = isEqual(calculatedColumns, widget.options?.calculatedColumns ?? [])
      ? widget.options
      : { ...widget.options, calculatedColumns };

    if (isEqual(widget.columns.names, revisedColumns) && revisedOptions === widget.options) {
      return widget;
    }

    const revisedWidget: WidgetDraft = {
      ...widget,
      columns: {
        ...widget.columns,
        names: revisedColumns,
      },
      options: revisedOptions,
    };

    if (!revisedWidget.columns.names.includes(revisedWidget.columns.sort_by)) {
      revisedWidget.columns.sort_by = revisedWidget.columns.names[0];
    }

    return revisedWidget;
  });

export const getContainerStyle = memoizeOne(
  (currentWidthBreakpoint: ViewportWidthBreakpoint, minimumWidgetWidth: number): CSSProperties => ({
    minWidth: currentWidthBreakpoint > ViewportWidthBreakpoint.Narrow ? minimumWidgetWidth + 40 : 0,
  })
);

/**
 * This calculates a reasonable max width for the widget list by taking the max width of two half
 * blocks plus a 32 pixel gap between them.
 */
export const DEFAULT_MAX_WIDGET_LIST_WIDTH = cssVariables.halfBlockMaxWidth * 2 + 32;

export const getContentStyle = memoizeOne(
  (minimumWidgetWidth: number): CSSProperties => ({
    maxWidth: Math.max(minimumWidgetWidth, DEFAULT_MAX_WIDGET_LIST_WIDTH),
  })
);

export const createEmptyEntityDataResponse = (): EntityDataResponse => ({
  totals: {
    duration: { ...EMPTY_DURATION },
    estimated_duration: { ...EMPTY_DURATION },
    billed_duration: { ...EMPTY_DURATION },
    unbilled_duration: { ...EMPTY_DURATION },
    cost: { ...EMPTY_COST },
    estimated_cost: { ...EMPTY_COST },
    billed_cost: { ...EMPTY_COST },
    unbilled_cost: { ...EMPTY_COST },
  },
  clients: [],
  users: [],
  labels: [],
  days: [],
  teams: [],
  linked_work: [],
  multiple_currency_report: false,
});

/**
 * Removes disallowed columns and replaces aliased columns in API payloads.
 */
export const transformExportedReportColumns = <T extends Column | ExportColumnId>(
  columns: Array<T>,
  features: Features
): Array<T> =>
  columns.reduce<Array<T>>((columns, column) => {
    // Hide the budget column from exports if the budget feature isn't enabled
    if (column === COLUMN_BUDGET && !features.project_budget) {
      return columns;
    }

    columns.push(
      ((COLUMN_EXPORT_ALIASES as Record<string, string>)[column as string] as T) ?? column
    );
    return columns;
  }, []);

export const TIMESTAMP_TOOLTIP_CUTOFF = 3;

export const createHourNotes = (
  data: {
    date?: string;
    from?: string | null;
    to?: string | null;
    timestamps?: Array<Pick<HourTimestamp, 'id' | 'from' | 'to'>>;
  },
  options: { includesDate: boolean; includesTimestamps: boolean; cutoff: number } = {
    includesDate: false,
    includesTimestamps: false,
    cutoff: TIMESTAMP_TOOLTIP_CUTOFF,
  }
) => {
  const hourNote = [];
  let additionalTimestamps: Array<string> = [];

  const hasDate = data.date && options.includesDate;
  if (hasDate) {
    hourNote.push(data.date);
  }
  if (data.timestamps && data.timestamps.length > 0 && options.includesTimestamps) {
    const timestampStrings = data.timestamps.reduce<{
      fullNote: string;
      truncatedTimestampNote: Array<string>;
    }>(
      (prev, _data, i) => {
        if (!data.timestamps) return prev;
        if (!prev.fullNote.length) {
          return {
            ...prev,
            fullNote: (prev.fullNote += ` at ${_data.from} - ${_data.to}`),
          };
        }

        if (i < options.cutoff) {
          return {
            ...prev,
            fullNote: (prev.fullNote += `, ${_data.from} - ${_data.to}`),
          };
        }

        if (i === options.cutoff) {
          return {
            fullNote: (prev.fullNote += ` + ${data.timestamps.length - options.cutoff} more`),
            truncatedTimestampNote: [...prev.truncatedTimestampNote, `${_data.from} - ${_data.to}`],
          };
        }

        return {
          fullNote: prev.fullNote,
          truncatedTimestampNote: [...prev.truncatedTimestampNote, `${_data.from} - ${_data.to}`],
        };
      },
      { fullNote: '', truncatedTimestampNote: [] }
    );

    hourNote.push(timestampStrings.fullNote);
    additionalTimestamps = timestampStrings.truncatedTimestampNote;
  } else if (data.from && data.to && options.includesTimestamps) {
    hourNote.push(hasDate ? ' at ' : null, `${data.from} - ${data.to}`);
  }

  return {
    additionalTimestamps,
    hourNote,
  };
};

export function areWidgetRowsExpandable(widgetType: string, isHidingEntries?: boolean): boolean {
  const nonExpandableWidgets = [WIDGET_CLIENTS_HBAR, WIDGET_TEAMS_LIST];
  const isExpandable = !nonExpandableWidgets.includes(widgetType);
  return isExpandable && !isHidingEntries;
}

export function getRowIdsFromWidget(widget: WidgetWithData | undefined): Array<number> {
  return (widget?.rows || [])
    .flatMap(row => {
      const subRows = row.rows || row.projects;
      return subRows ? subRows.map(subRow => subRow.id as number) : row.id;
    })
    .filter(notNull);
}

export function hourToGenericWidgetValues(hour: Hour<unknown>) {
  const isBilled = hour.billed;

  return {
    loggedMinutes: hour.duration.total_minutes,
    loggedMoney: hour.cost?.amount,
    plannedMinutes: hour.estimated_duration.total_minutes,
    plannedMoney: hour.estimated_cost?.amount,
    billedMinutes: isBilled ? hour.duration.total_minutes : undefined,
    billedMoney: isBilled ? hour.cost?.amount : undefined,
    unbilledMinutes: !isBilled ? hour.duration.total_minutes : undefined,
    unbilledMoney: !isBilled ? hour.cost?.amount : undefined,
    billableHours: hour.billable ? hour.duration.total_hours : undefined,
    nonBillableHours: !hour.billable ? hour.duration.total_hours : undefined,
  };
}

export function formatGenericWidgetValues(
  values: ReturnType<typeof hourToGenericWidgetValues>,
  currency: Currency
): Partial<GenericWidgetRowValues> {
  return {
    ...values,
    loggedMoney: formatCurrency(currency, values.loggedMoney, 2),
    plannedMoney: formatCurrency(currency, values.plannedMoney, 2),
    billedMoney: values.billedMoney ? formatCurrency(currency, values.billedMoney, 2) : undefined,
    unbilledMoney: values.unbilledMoney
      ? formatCurrency(currency, values.unbilledMoney, 2)
      : undefined,
  };
}

export function excludeReadonlyHours(
  selection: ReportSelection,
  hours: Record<number, Hour<number>>
): ReportSelection {
  return {
    ...selection,
    hourIds: selection.hourIds.filter(hourId => {
      const hour = hours[hourId];
      return !!hour?.manage;
    }),
  };
}

/**
 * Legacy Hour States dropdown selection is transformed to set of state ids for the Hour States feature.
 * Billed state is a combination of Billed, Billed & Locked and Invoiced states.
 * Locked state is a combination of Locked, Billed & Locked and Invoiced states.
 */
export const transformLegacyFiltersToStateIds = (
  filters: ReportQuery['filters'],
  hourStates: Record<number, HourState>
): ReportFiltersWithoutLegacyProps => {
  const { state_ids, ...rest } = filters;
  const states = Object.values(hourStates);

  const getStateId = (name: string) => states.find(state => state.name === name)!.id;

  const loggedStateId = getStateId(HOUR_STATE_LOGGED);
  const billedStateId = getStateId(HOUR_STATE_BILLED);
  const lockedStateId = getStateId(HOUR_STATE_LOCKED);
  const billedAndLockedStateId = getStateId(HOUR_STATE_BILLED_AND_LOCKED);
  const invoicedStateId = getStateId(HOUR_STATE_INVOICED);

  const allBilledStateIds = [billedStateId, billedAndLockedStateId];
  const allLockedStateIds = [lockedStateId, billedAndLockedStateId];

  const logged = state_ids.includes(loggedStateId);
  const billed = state_ids.some(id => allBilledStateIds.includes(id));
  const locked = state_ids.some(id => allLockedStateIds.includes(id));
  const invoiced = state_ids.includes(invoicedStateId);

  let stateIds: Array<number> = [];
  if (billed && locked) {
    stateIds = [...allBilledStateIds, lockedStateId];
  } else if (billed) {
    stateIds = allBilledStateIds;
  } else if (locked) {
    stateIds = allLockedStateIds;
  }

  if (invoiced) stateIds.push(invoicedStateId);
  if (logged) stateIds.push(loggedStateId);

  return { ...rest, state_ids: stateIds };
};

export const getLocalStorageTimesheetColumnSettingsKey = (userId: number) =>
  `USER_${userId}_TIMESHEET_COLUMNS`;

export const getProjectFilterLabel = (
  projectIdsOrStatus: ReportProjectIds,
  numTotalProjects: number,
  numActiveProjects: number
) => {
  if (Array.isArray(projectIdsOrStatus)) {
    if (projectIdsOrStatus.length === 0) {
      return 'Any project';
    }
    return `${projectIdsOrStatus.length} projects`;
  } else if (projectIdsOrStatus === 'all') {
    return `All ${numTotalProjects} projects`;
  } else if (projectIdsOrStatus === 'active') {
    return `${numActiveProjects} active projects`;
  } else if (projectIdsOrStatus === 'archived') {
    return `${numTotalProjects - numActiveProjects} archived projects`;
  }

  return 'Any project';
};

export const filterProjectsByStatusOrIds = (
  projectIds: ReportProjectIds,
  projectMap: Record<number, ProjectVariant | undefined>
) => {
  if (Array.isArray(projectIds)) {
    return projectIds.map(id => getFullProjectOrNull(projectMap[id])).filter(notNull);
  } else if (projectIds === 'all') {
    return Object.keys(projectMap)
      .map(id => getFullProjectOrNull(projectMap[+id]))
      .filter(notNull);
  } else if (projectIds === 'active') {
    return Object.keys(projectMap)
      .map(id => getFullProjectOrNull(projectMap[+id]))
      .filter(notNull)
      .filter(project => project.active);
  } else if (projectIds === 'archived') {
    return Object.keys(projectMap)
      .map(id => getFullProjectOrNull(projectMap[+id]))
      .filter(notNull)
      .filter(project => !project.active);
  }

  return [];
};
interface FilterStatusResult {
  hasModifiedFilters: boolean;
  buttonText: string;
}
interface FilterCounts {
  projects: number;
  users: number;
  labels: number;
  teams: number;
  hourStates: number;
  totalTags: number;
  totalTeams: number;
  totalProjects: number;
  activeProjects: number;
}

export const determineFilterStatus = (
  query: ReportQuery,
  counts: FilterCounts
): FilterStatusResult => {
  // Check for zero selections
  const zeroSelectedUsers = !counts.users;
  const zeroSelectedLabels = !counts.labels;
  const zeroSelectedTeams = !counts.teams;
  const zeroSelectedHourStates = !counts.hourStates;
  const zeroSelectedProjects = !counts.projects;

  // Determine active/modified states
  const hasActiveTeamFilter = counts.teams > 0;
  const hasModifiedTeamFilter = counts.teams !== counts.totalTeams && !zeroSelectedTeams;
  const hasModifiedProjectsFilter = !zeroSelectedProjects;
  const hasModifiedUserFilter =
    !hasActiveTeamFilter && counts.users !== counts.totalTeams && !zeroSelectedUsers;
  const hasModifiedLabelsFilter = counts.labels !== counts.totalTags + 1 && !zeroSelectedLabels; // for 0/virtualtag
  const hasModifiedBilledState = query.filters.billed !== null;
  const hasModifiedHourStateFilter = !zeroSelectedHourStates;
  const hasModifiedExternalLinkFilter = !!query.filters.external_link_filter;
  const hasModifiedSyncStatusFilter = !!query.filters.sync_status_filter;

  // Determine overall modified state
  const hasModifiedFilters =
    hasModifiedProjectsFilter ||
    hasModifiedUserFilter ||
    (hasModifiedTeamFilter && hasActiveTeamFilter) ||
    hasModifiedLabelsFilter ||
    hasModifiedBilledState ||
    hasModifiedHourStateFilter ||
    hasModifiedExternalLinkFilter ||
    hasModifiedSyncStatusFilter;

  // Build filter labels
  const filterLabels: Array<string> = [];
  if (hasModifiedUserFilter) {
    filterLabels.push(`${counts.users} users`);
  }
  if (hasModifiedTeamFilter && hasActiveTeamFilter) {
    filterLabels.push(`${counts.teams} teams`);
  }
  if (hasModifiedProjectsFilter) {
    filterLabels.push(
      getProjectFilterLabel(query.filters.project_ids, counts.totalProjects, counts.activeProjects)
    );
  }
  if (hasModifiedLabelsFilter) {
    filterLabels.push(`${counts.labels} tags`);
  }
  if (hasModifiedBilledState) {
    const billedState = query.filters.billed ? 'billed only' : 'unbilled only';
    filterLabels.push(billedState);
  }
  if (hasModifiedHourStateFilter) {
    filterLabels.push(`${counts.hourStates} states`);
  }
  if (hasModifiedExternalLinkFilter || hasModifiedSyncStatusFilter) {
    filterLabels.push('linked work/sync');
  }

  return {
    hasModifiedFilters,
    buttonText: !hasModifiedFilters ? 'Filters' : filterLabels.join(', '),
  };
};
